import { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { isPast, secondsToMilliseconds } from 'date-fns';

import { selectAppState } from 'store/config/selectors';
import { RBProgram } from 'store/railsBuilder/reducer.types';

import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import ticker$ from 'utils/hooks/useInterval';

type Timer = {
    unsubscribe: Function;
};

type Props = {
    program?: RBProgram;
    updateFrequency?: number;
    startTime?: number;
    endTime?: number;
};

export default function useIsLive({
    program,
    startTime,
    endTime,
    updateFrequency = 5000,
}: Props) {
    let { programStartTime = startTime, programEndTime = endTime } = program
        ? getCorrectProgramStartAndEndTime(program)
        : {};

    if (programStartTime && programEndTime) {
        programStartTime = secondsToMilliseconds(programStartTime);
        programEndTime = secondsToMilliseconds(programEndTime);
    }

    const appState = useSelector(selectAppState);

    const timer = useRef<Timer | undefined>(undefined);
    const [isLive, setIsLive] = useState(false);

    const unsetTimer = useCallback(() => {
        if (timer.current) {
            timer.current.unsubscribe();
            timer.current = undefined;
        }
    }, []);

    const onTick = useCallback(() => {
        if (programStartTime && programEndTime) {
            const now = Date.now();

            if (now >= programStartTime && now < programEndTime) {
                setIsLive(true);
            } else {
                setIsLive(false);

                if (now > programEndTime) {
                    unsetTimer();
                }
            }
        }
    }, [programStartTime, programEndTime, unsetTimer]);

    useEffect(() => {
        unsetTimer();

        if (
            programStartTime &&
            programEndTime &&
            !isPast(programEndTime) &&
            appState === 'active'
        ) {
            timer.current = ticker$(updateFrequency).subscribe(onTick);
        } else if (appState === 'active') {
            setIsLive(false);
        } else if (appState === 'background') {
            unsetTimer();
        }

        return () => {
            unsetTimer();
        };
    }, [appState, programStartTime, programEndTime, onTick, unsetTimer]);

    return isLive;
}
